import React, { useState } from 'react';
import styled from 'styled-components';
import { FaQuestion, FaBook, FaGamepad } from 'react-icons/fa';
import TutorialOverlay from './TutorialOverlay';
import VisualBattleGuide from './VisualBattleGuide';

const HelpContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;

  body.in-match & {
    display: none;
  }
`;

/* FAB — amber hex-shaped button on ink ground, matching mass-poster CTAs */
const HelpButton = styled.button`
  width: 56px;
  height: 56px;
  background: var(--color-amber-500);
  border: 1px solid var(--color-ink-950);
  color: var(--color-ink-950);
  font-size: 22px;
  cursor: pointer;
  clip-path: var(--clip-button);
  box-shadow: var(--shadow-glow-amber), var(--shadow-md);
  transition: transform var(--dur-fast) var(--ease-out),
              background var(--dur-fast) var(--ease-out);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--color-amber-400);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const HelpMenu = styled.div`
  position: absolute;
  bottom: 72px;
  right: 0;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  padding: 22px 20px 18px;
  min-width: 300px;
  box-shadow: var(--shadow-lg);
  color: var(--color-cream-100);

  /* top amber rail to keep the card tethered to the FAB's palette */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
  }
`;

const MenuTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 0 14px 0;
  padding: 0 28px 10px;
  font-family: var(--font-heading);
  font-size: 13px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
  border-bottom: 1px solid var(--color-ink-700);

  &::after {
    content: '////';
    font-family: var(--font-display);
    font-style: italic;
    font-size: 11px;
    letter-spacing: -0.08em;
    color: var(--color-amber-500);
    opacity: 0.75;
  }
`;

const MenuButton = styled.button`
  width: 100%;
  background: var(--color-ink-800);
  border: 1px solid var(--color-ink-700);
  color: var(--color-cream-100);
  padding: 10px 14px;
  margin-bottom: 8px;
  cursor: pointer;
  clip-path: var(--clip-button);
  transition: background var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: var(--weight-semibold);
  text-align: left;

  &:hover {
    background: var(--color-ink-700);
    border-color: var(--color-amber-500);
    transform: translateX(3px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  .icon {
    font-size: 16px;
    color: var(--color-amber-500);
    flex-shrink: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  background: transparent;
  border: none;
  color: var(--color-cream-400);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  transition: color var(--dur-fast) var(--ease-out);

  &:hover {
    color: var(--color-amber-500);
  }
`;

const HelpSystem = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showBattleGuide, setShowBattleGuide] = useState(false);

  const handleTutorialStart = () => {
    setShowTutorial(true);
    setShowMenu(false);
  };

  const handleBattleGuideOpen = () => {
    setShowBattleGuide(true);
    setShowMenu(false);
  };

  const handleTutorialClose = () => {
    setShowTutorial(false);
  };

  return (
    <>
      <HelpContainer>
        <HelpButton onClick={() => setShowMenu(!showMenu)}>
          <FaQuestion />
        </HelpButton>
        
        {showMenu && (
          <HelpMenu>
            <CloseButton onClick={() => setShowMenu(false)}>
              &times;
            </CloseButton>
            <MenuTitle>How to Play</MenuTitle>
            
            <MenuButton onClick={handleBattleGuideOpen}>
              <FaGamepad className="icon" />
              <div>
                <div>Battle Guide</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Game mechanics, turn order, strategy
                </div>
              </div>
            </MenuButton>

            <MenuButton onClick={handleTutorialStart}>
              <FaBook className="icon" />
              <div>
                <div>Tour the UI</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Quick homepage walkthrough
                </div>
              </div>
            </MenuButton>
          </HelpMenu>
        )}
      </HelpContainer>
      
      <TutorialOverlay
        isOpen={showTutorial}
        onClose={handleTutorialClose}
        tutorialType="main"
      />

      <VisualBattleGuide
        isOpen={showBattleGuide}
        onClose={() => setShowBattleGuide(false)}
      />
    </>
  );
};

export default HelpSystem;
